import React, { useEffect } from 'react';
import { Modal, Button, Form as BootstrapForm, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form as FormikForm, Field } from 'formik';
import * as Yup from 'yup';

import { FormGroupControl } from '@components/Base';
import { requestDocuments, requestDocumentsReset } from './action';
import { selectRequestDocumentsModal } from './selector';
import i18n from '@components/i18n';
import { camelCase } from 'lodash';

const DOCUMENT_TYPES = [
  'claim_form_completed_signed',
  'medical_certificate',
  'prescription_with_diagnosis',
  'laboratory_test_results',
  'imaging_results',
  'medical_certificate_discharge_summary',
  'hospital_discharge_summary',
  'detailed_invoice_receipt_stamped_paid',
  'doctor_appointment_proof',
  'medication_prescription',
  'eye_examination_auto_refractor_result',
  'dental_xray_panorex_film',
  'health_checkup_report',
  'vaccination_record_certificate',
  'biology_test_results',
  'doctors_diagnosis_report',
  'authorization_letter_claim_payment',
  'payee_identification_document',
  'national_id_card_or_passport',
];

interface RequestDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimableType: string;
  claimableId: number;
  claimNumber: string;
}

interface FormValues {
  documentTypes: string[];
  note: string;
}

const groupProps = { className: 'mb-3' };

const validationSchema = Yup.object({
  documentTypes: Yup.array().of(Yup.string()),
  note: Yup.string(),
}).test(
  'note-or-documents',
  i18n.t('requestDocumentsModal.validation.noteOrDocumentsRequired'),
  (values) => (values.documentTypes && values.documentTypes.length > 0) || (values.note && values.note.trim().length > 0)
);

const initialValues: FormValues = {
  documentTypes: [],
  note: '',
};

const RequestDocumentsModal = ({
  isOpen,
  onClose,
  claimableType,
  claimableId,
  claimNumber,
}: RequestDocumentsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isSubmitting, isSuccess } = useSelector(selectRequestDocumentsModal);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(requestDocumentsReset());
    }
  }, [isOpen, dispatch]);

  const handleSubmit = (values: FormValues) => {
    dispatch(
      requestDocuments({
        claimableType,
        claimableId,
        documentTypes: values.documentTypes,
        note: values.note || undefined,
      }),
    );
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      aria-labelledby="request-documents-modal-title"
      centered
      size="lg"
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isValid }) => (
          <FormikForm>
            <Modal.Header closeButton>
              <Modal.Title id="request-documents-modal-title">
                {t('requestDocumentsModal.title', { claimNumber })}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <BootstrapForm.Group className="mb-4">
                <BootstrapForm.Label className="fw-bold">
                  {t('requestDocumentsModal.documentTypes')}
                </BootstrapForm.Label>
                <BootstrapForm.Text className="d-block mb-2 text-muted">
                  {t('requestDocumentsModal.helperText')}
                </BootstrapForm.Text>
                <Row>
                  {DOCUMENT_TYPES.map((docType) => (
                    <Col xs={12} md={6} key={docType}>
                      <Field name="documentTypes">
                        {() => (
                          <BootstrapForm.Check
                            type="checkbox"
                            id={`doc-type-${docType}`}
                            label={t(`documentTypes.${camelCase(docType)}`)}
                            checked={values.documentTypes.includes(docType)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              if (e.target.checked) {
                                setFieldValue('documentTypes', [...values.documentTypes, docType]);
                              } else {
                                setFieldValue(
                                  'documentTypes',
                                  values.documentTypes.filter((d) => d !== docType),
                                );
                              }
                            }}
                            className="mb-2"
                          />
                        )}
                      </Field>
                    </Col>
                  ))}
                </Row>
              </BootstrapForm.Group>

              <FormGroupControl
                name="note"
                as="textarea"
                rows={3}
                label={t('requestDocumentsModal.note')}
                groupProps={groupProps}
                placeholder={t('requestDocumentsModal.notePlaceholder')}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting || !isValid}>
                {isSubmitting ? t('common:sending') : t('requestDocumentsModal.submit')}
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export { RequestDocumentsModal };
